import type { FimAssetInstanceEntry, FimSubjectSummary } from 'features/fim'
import { subjectUidFromEntryId } from 'features/fim/subjectUid'

/**
 * One catalog row: the live Subject is canonical for identity and filtering;
 * the Asset Instance overlays lifecycle and fill information only. "Promoted"
 * is derived from instance existence, in any fill state.
 */
export interface FacilityModelRecord {
  subjectUid: string
  subject: FimSubjectSummary
  assetInstance?: FimAssetInstanceEntry
}

/** Display subsystem, off the projection field. */
export const subjectSubsystem = (
  subject: FimSubjectSummary
): string | undefined => subject.data.subsystem_id ?? undefined

export type FacilityModelDataIssueReason =
  | 'unresolved-subject'
  | 'duplicate-instance'

export interface FacilityModelDataIssue {
  instance: FimAssetInstanceEntry
  reason: FacilityModelDataIssueReason
}

/**
 * Instances keyed by the subject_uid they name. An instance whose subject was
 * superseded keeps naming the uid it was minted against, so it simply misses
 * every live subject — it is detached, and where it went is resolved one
 * instance at a time from the data-issues surface rather than by loading the
 * site's whole supersede lineage on the catalog's first-paint path.
 */
export type InstanceIndex = ReadonlyMap<string, FimAssetInstanceEntry>

export function indexInstancesByUid(
  instances: readonly FimAssetInstanceEntry[]
): InstanceIndex {
  const index = new Map<string, FimAssetInstanceEntry>()
  for (const instance of instances) {
    const uid = instance.data.subject_uid
    if (uid && !index.has(uid)) index.set(uid, instance)
  }
  return index
}

/**
 * The `uids=` constraint for instance-backed reads: every uid an instance
 * names (the server drops the ones its run does not hold).
 */
export function instanceSubjectUids(
  instances: readonly FimAssetInstanceEntry[]
): string[] {
  return [
    ...new Set(
      instances.map((instance) => instance.data.subject_uid).filter(Boolean)
    ),
  ]
}

/**
 * Attach site Asset Instances to a PAGE of Subject rows. A page row's uid is
 * known to exist, so this is a direct match; anything that lands on no row in
 * this page is simply not attached (full-run detachment detection lives in
 * `computeInstanceDataIssues`).
 */
export function attachInstancesToPage(
  subjects: readonly FimSubjectSummary[],
  instances: InstanceIndex
): FacilityModelRecord[] {
  return subjects.map((subject) => {
    const uid = subjectUidFromEntryId(subject.id)
    const attached = instances.get(uid)
    return {
      subjectUid: uid,
      subject,
      ...(attached && { assetInstance: attached }),
    }
  })
}

export interface InstanceJoin {
  /** Current run uid → the instance naming it. */
  attachByUid: Map<string, FimAssetInstanceEntry>
  dataIssues: FacilityModelDataIssue[]
}

/**
 * Full-run instances-vs-Subjects diff against the set of uids KNOWN to exist
 * in the published run (from a by-ids presence read), producing the per-uid
 * attach map and the data issues the catalog holds out of normal rows. An
 * instance naming a uid the run does not hold is detached: fusion moved its
 * subject, and `unresolved-subject` is where that surfaces.
 */
export function computeInstanceDataIssues(
  instances: readonly FimAssetInstanceEntry[],
  presentUids: ReadonlySet<string>
): InstanceJoin {
  const attachByUid = new Map<string, FimAssetInstanceEntry>()
  const dataIssues: FacilityModelDataIssue[] = []

  for (const instance of instances) {
    const uid = instance.data.subject_uid
    if (!uid || !presentUids.has(uid)) {
      dataIssues.push({ instance, reason: 'unresolved-subject' })
      continue
    }
    if (attachByUid.has(uid)) {
      dataIssues.push({ instance, reason: 'duplicate-instance' })
      continue
    }
    attachByUid.set(uid, instance)
  }

  return { attachByUid, dataIssues }
}
